import type { BlogItem } from 'features/DigitalMarketing/slice/types';

export type BlogView = 'dashboard' | 'create' | 'preview';

export type BlogPublishFilter = 'published' | 'saved' | 'both';

export type BlogForm = {
    title: string;
    description: string;
    content: string;
    image: string;
    coverImageAlt: string;
};

export const emptyBlogForm: BlogForm = {
    title: '',
    description: '',
    content: '',
    image: '',
    coverImageAlt: '',
};

export const blogFilterOptions: Array<{ label: string; value: BlogPublishFilter }> = [
    { label: 'All Blogs', value: 'both' },
    { label: 'Published Only', value: 'published' },
    { label: 'Saved Only', value: 'saved' },
];

export const getBlogRouteState = (pathname: string) => {
    const pathSegments = pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    const blogId = lastSegment && !Number.isNaN(Number(lastSegment)) ? Number(lastSegment) : null;
    const isEditMode = pathname.includes('/edit/');
    const isPreviewView = pathname.includes('/preview');
    const currentView: BlogView = pathname.includes('/create') || isEditMode
        ? 'create'
        : isPreviewView
            ? 'preview'
            : 'dashboard';

    return {
        currentView,
        isEditMode,
        isPreviewView,
        blogId,
        hasIdInUrl: blogId !== null,
    };
};

export const getBlogId = (blog?: BlogItem | null): number | null => {
    if (!blog) {
        return null;
    }

    return blog.blog_id
        ?? blog.BRANCH_BLOG_ID
        ?? blog.branch_blog_id
        ?? blog.BLOG_ID
        ?? blog.id
        ?? blog.ID
        ?? null;
};

export const isBlogPublished = (blog?: BlogItem | null) => {
    const value = blog?.is_published
        ?? blog?.IS_PUBLISHED
        ?? blog?.is_web_published
        ?? blog?.IS_WEB_PUBLISHED
        ?? blog?.is_published_website
        ?? blog?.IS_PUBLISHED_WEBSITE
        ?? blog?.published
        ?? blog?.PUBLISHED
        ?? blog?.status
        ?? blog?.STATUS;

    if (typeof value === 'boolean') {
        return value;
    }

    const normalizedValue = String(value ?? '').trim().toLowerCase();

    return value !== undefined
        && value !== null
        && normalizedValue !== ''
        && !['false', '0', 'no', 'unpublished', 'unpublish', 'draft'].includes(normalizedValue);
};

export const getBlogFormFromBlog = (blog?: BlogItem | null): BlogForm => ({
    title: blog?.title || blog?.TITLE || '',
    description: blog?.excerpt || blog?.EXCERPT || blog?.description || '',
    content: blog?.content || blog?.CONTENT || '',
    image: blog?.cover_image_url || blog?.COVER_IMAGE_URL || blog?.image || '',
    coverImageAlt: blog?.cover_image_alt || blog?.COVER_IMAGE_ALT || '',
});

export const hasBlogFormChanges = (currentForm: BlogForm, originalBlog?: BlogItem | null) => {
    if (!originalBlog) {
        return true;
    }

    const originalForm = getBlogFormFromBlog(originalBlog);

    return (
        currentForm.title !== originalForm.title ||
        currentForm.description !== originalForm.description ||
        currentForm.content !== originalForm.content ||
        currentForm.image !== originalForm.image
    );
};

export const buildBlogUpsertPayload = ({
    form,
    blogId,
}: {
    form: BlogForm;
    blogId?: number | null;
}) => ({
    TITLE: form.title || '',
    CONTENT: form.content,
    COVER_IMAGE_URL: form.image,
    ...(blogId && { blog_id: blogId }),
});

export const extractGeneratedImageUrl = (generatedImageUrl: unknown): string => {
    if (!generatedImageUrl) {
        return '';
    }

    if (typeof generatedImageUrl === 'object') {
        const imageResponse = generatedImageUrl as {
            data?: string | string[] | { data?: string };
            url?: string;
        };

        if (typeof imageResponse.data === 'string') {
            return imageResponse.data;
        }

        if (Array.isArray(imageResponse.data)) {
            return imageResponse.data[0] || '';
        }

        return imageResponse.url || imageResponse.data?.data || '';
    }

    if (typeof generatedImageUrl !== 'string') {
        return '';
    }

    if (generatedImageUrl.startsWith('http')) {
        return generatedImageUrl;
    }

    try {
        const parsed = JSON.parse(generatedImageUrl);
        const urlData = parsed?.data || parsed?.url || parsed;

        if (typeof urlData === 'string') {
            return urlData;
        }

        if (Array.isArray(urlData)) {
            return urlData[0] || '';
        }

        return urlData?.data || '';
    } catch (error) {
        return generatedImageUrl;
    }
};

export const getCacheBustedImageUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;

export const toBlogCardModel = (blog: BlogItem) => {
    const blogId = getBlogId(blog);

    return {
        blogId,
        blog: {
            ...blog,
            id: blogId ?? 0,
            image: blog.cover_image_url || blog.COVER_IMAGE_URL || blog.image || 'https://placehold.co/600x400/E1DCFD/5E4DE0?text=No+Image',
            title: blog.title || blog.TITLE || blog.excerpt || blog.EXCERPT || 'No title available',
        },
    };
};
